"use client";

import { useRouterState } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import Loader from "@/components/loader";
import { Overlay } from "@/components/ui/_internal/overlay";
import { Button } from "@/components/ui/button";
import { getRedirectPathFromLocation } from "@/lib/redirect-after-login";

// Overlay mounts its contents only when opened. Keep the form and phone parsing
// outside the global entry bundle without delaying the closeable dialog shell.
const SignUpForm = lazy(() => import("@/components/sign-up-form"));

export function GuestSignupDialog({
  onOpenChange,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useTranslations("globals.auth");
  const location = useRouterState({ select: (state) => state.location });
  const redirectPath = getRedirectPathFromLocation(location);

  return (
    <Overlay
      open={open}
      onOpenChange={onOpenChange}
      aria-labelledby="guest-signup-dialog-title"
      className="overflow-x-hidden"
    >
      <header className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 id="guest-signup-dialog-title" className="text-lg font-bold">
          {t("guestBannerTitle")}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("guestSignupModalClose")}
          onClick={() => onOpenChange(false)}
        >
          <CloseIcon decorative />
        </Button>
      </header>
      <div className="p-6">
        <Suspense fallback={<Loader />}>
          <SignUpForm
            redirectAfterLogin={redirectPath}
            showHeader={false}
            onSuccess={() => onOpenChange(false)}
          />
        </Suspense>
      </div>
    </Overlay>
  );
}
